import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Container,
  Avatar,
  Button,
  Tooltip,
  MenuItem,
  CircularProgress,
  Dialog,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  Stack,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  useMediaQuery,
  Menu,
  ListItemIcon,
} from '@mui/material';
import {
  Logout,
  Menu as MenuIcon,
  NotificationsNone as NotificationsNoneIcon,
  Settings,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { logoutAction, updateTokenAction } from '../Onboarding/auth/action';
import { decodeToken, secondaryPermission } from '../../utils/settings';
import { useEffect, useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import useOnboardCheck from './Hooks/useOnboardCheck';
import { deleteRegisterNotificationToken } from '../../screen/distributor/action/util_action';
import HumbeeLogo from './HumbeeLogo';

function TopNav() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:1279px)');

  const { loginUser, secondaryPermissionsRoles } = useSelector((state) => ({
    loginUser: state.loginReducer.loginUser,
    secondaryPermissionsRoles: state.loginReducer.secondaryPermissionsRoles,
  }));

  const ORDER_ADMIN = secondaryPermissionsRoles?.includes(
    secondaryPermission['ORDER_ADMIN'],
  );
  const PAYMENT_ADMIN = secondaryPermissionsRoles?.includes(
    secondaryPermission['PAYMENT_ADMIN'],
  );
  const COMPANY_ADMIN = secondaryPermissionsRoles?.includes(
    secondaryPermission['COMPANY_ADMIN'],
  );

  const pages = [
    {
      name: 'My Business',
      path: '/order-booking/po-select-industry',
      isVisible: PAYMENT_ADMIN || ORDER_ADMIN || COMPANY_ADMIN,
    },
    {
      name: 'Onboard Buyer',
      path: '/onboard-buyer',
      isVisible: ORDER_ADMIN || COMPANY_ADMIN,
    },
    {
      name: 'HUMBEE Benefits',
      path: '/humbee-benefits',
      isVisible: PAYMENT_ADMIN || ORDER_ADMIN || COMPANY_ADMIN,
    },
    {
      name: 'My Benefits',
      path: '/benefits/mybenefits',
      isVisible: COMPANY_ADMIN,
    },
    {
      name: 'My Performance',
      path: '/my-performance/Performance',
      isVisible: PAYMENT_ADMIN || ORDER_ADMIN || COMPANY_ADMIN,
    },
    {
      name: 'My Account',
      path: '/my-account/user-details',
      isVisible: isMobile && loginUser,
    },
  ].filter((page) => page.isVisible);

  const [loading, setLoading] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState({});
  const [decodedData, setDecodedData] = useState({});
  const checkOnboarding = useOnboardCheck(loginUser?.is_firm_onboarded);
  const [openLogoutDialog, setOpenLogoutDialog] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false); // State for drawer
  const handleClose = () => {
    setOpenLogoutDialog(false);
  };

  const userName = loginUser?.first_name || 'Guest';
  const isLoggedIn = !!loginUser;

  useEffect(() => {
    const getTokenData = async () => {
      const decoded = await decodeToken();
      setDecodedData(decoded);
      setSelectedCompany(decoded?.default_company_info?.company_id);
    };
    getTokenData();
  }, []);

  const handleCompanyChange = async (event) => {
    setLoading(true);
    const { data, error, message } = await updateTokenAction({
      company_id: event.target.value,
    });

    setTimeout(() => setLoading(false), 500);

    if (!error) {
      setSelectedCompany(event.target.value);
      localStorage.setItem('token', data);
      navigate('/', { replace: true });
    } else {
      toast.error(message);
    }
  };
  const onLogout = async () => {
    const userToken = JSON.parse(localStorage.getItem('message_token'));
    await deleteRegisterNotificationToken(userToken);
    await logoutAction();
    localStorage.clear();
    dispatch({ type: 'LOGOUT' });
    setOpenLogoutDialog(false);
    navigate('/', { replace: true });
  };
  function SimpleDialog(props) {
    const { onClose, open } = props;

    const handleClose = () => {
      onClose();
    };

    return (
      <Dialog
        onClose={handleClose}
        open={open}
      >
        <Box
          sx={{
            p: 3,
            px: 10,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <DialogTitle
            variant='h5'
            sx={{ fontWeight: 'bold' }}
          >
            Confirm Logout
          </DialogTitle>
          <Typography>Are you sure you want to log out?</Typography>

          <Stack
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              width: '100%',
              marginTop: 3,
            }}
          >
            <Button
              onClick={handleClose}
              variant='contained'
              color='error'
            >
              Cancel
            </Button>

            <Button
              onClick={onLogout}
              variant='contained'
            >
              Yes, Logout
            </Button>
          </Stack>
        </Box>
      </Dialog>
    );
  }
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseDropdown = () => {
    setAnchorEl(null);
  };
  return (
    <AppBar
      position='sticky'
      sx={{
        background: '#fff',
        boxShadow: '0 2px 0px #F2F2F2',
        color: 'black',
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: -2,
          left: 0,
          width: '100%',
          height: '4px',
          background: 'linear-gradient(to right, #000000 0%, #FF9201 55%)',
          zIndex: 101,
        },
      }}
    >
      <Container maxWidth='false'>
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box
            sx={{ cursor: 'pointer' }}
            onClick={() => navigate('/')}
          >
            <HumbeeLogo />
          </Box>
          {!isMobile && loginUser && (
            <>
              <Box sx={{ ml: 3, flexGrow: 1, display: 'flex' }}>
                {pages.map((page) => (
                  <>
                    <Button
                      key={page.name}
                      onClick={() =>
                        page.path === '/my-account/user-details'
                          ? navigate(page.path)
                          : checkOnboarding(page.path)
                      }
                      sx={{
                        my: 2,
                        color: 'black',
                        fontWeight: 'bold',
                        fontSize: '0.810rem',
                      }}
                    >
                      {page.name}
                    </Button>
                  </>
                ))}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {loading ? (
                  <CircularProgress
                    color='inherit'
                    sx={{ mr: 2 }}
                  />
                ) : (
                  loginUser &&
                  decodedData?.all_companies_info?.length > 0 && (
                    <FormControl sx={{ mx: 1, width: 150 }}>
                      <InputLabel>Select Firm</InputLabel>
                      <Select
                        label='Select Firm'
                        value={selectedCompany}
                        onChange={handleCompanyChange}
                        size='small'
                      >
                        {decodedData?.all_companies_info.map((company) => (
                          <MenuItem
                            key={company.company_id}
                            value={company.company_id}
                          >
                            {company.company_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )
                )}
                <IconButton
                  onClick={() => navigate('/distributor/notifications')}
                  sx={{ mr: 2, color: 'black' }}
                >
                  <NotificationsNoneIcon />
                </IconButton>
              </Box>
            </>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {!isMobile && (
              <>
                <Box>
                  <Tooltip title={loginUser && 'My Account'}>
                    <Button
                      sx={{ p: 0 }}
                      onClick={handleClick}
                      disabled={!loginUser}
                    >
                      <Avatar
                        alt={userName}
                        src={loginUser?.profile_image}
                      />
                      <Typography
                        sx={{
                          ml: 1,
                          maxWidth: 100,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          cursor: 'pointer',
                        }}
                        variant='overline'
                        title={userName}
                      >
                        {userName}
                      </Typography>
                    </Button>
                  </Tooltip>
                  {loginUser && (
                    <Menu
                      anchorEl={anchorEl}
                      id='account-menu'
                      open={open}
                      onClose={handleCloseDropdown}
                      onClick={handleCloseDropdown}
                      slotProps={{
                        paper: {
                          elevation: 0,
                          sx: {
                            overflow: 'visible',
                            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                            mt: 2,
                            '& .MuiAvatar-root': {
                              width: 32,
                              height: 32,
                              ml: -0.5,
                              mr: 1,
                            },
                            '&::before': {
                              content: '""',
                              display: 'block',
                              position: 'absolute',
                              top: 0,
                              right: 14,
                              width: 10,
                              height: 10,
                              bgcolor: 'background.paper',
                              transform: 'translateY(-50%) rotate(45deg)',
                              zIndex: 0,
                            },
                          },
                        },
                      }}
                      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    >
                      <MenuItem
                        onClick={() => {
                          navigate('/my-account/user-details');
                        }}
                      >
                        <ListItemIcon>
                          <Settings fontSize='small' />
                        </ListItemIcon>
                        My Account
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          setOpenLogoutDialog(true);
                        }}
                      >
                        <ListItemIcon>
                          <Logout fontSize='small' />
                        </ListItemIcon>
                        Logout
                      </MenuItem>
                    </Menu>
                  )}
                </Box>
                {!loginUser && (
                  <Button
                    variant='contained'
                    sx={{
                      ml: 2,
                      borderRadius: 10,
                    }}
                    onClick={() => navigate('/login')}
                  >
                    {'Login'}
                  </Button>
                )}
              </>
            )}
            {isMobile && (
              <>
                <IconButton
                  onClick={() => setOpenDrawer(true)}
                  sx={{ ml: 2 }}
                >
                  <MenuIcon />
                </IconButton>
                {loginUser && (
                  <IconButton
                    onClick={() => navigate('/distributor/notifications')}
                    sx={{ mr: 2, color: 'black' }}
                  >
                    <NotificationsNoneIcon />
                  </IconButton>
                )}
              </>
            )}
          </Box>
        </Toolbar>
        <Drawer
          anchor='right'
          open={openDrawer}
          onClose={() => setOpenDrawer(false)}
        >
          <Box sx={{ width: 250, p: 2 }}>
            <Box sx={{ mb: 2, pl: 2 }}>
              <Tooltip title='Profile'>
                <IconButton sx={{ p: 0 }}>
                  <Avatar
                    alt={userName}
                    src={loginUser?.profile_image}
                  />
                </IconButton>
              </Tooltip>
              <Typography
                sx={{
                  fontWeight: 'bold',
                  ml: 1,
                  maxWidth: 100,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {userName}
              </Typography>
            </Box>

            {loading ? (
              <CircularProgress
                color='inherit'
                sx={{ mr: 2 }}
              />
            ) : (
              loginUser &&
              decodedData?.all_companies_info?.length > 0 && (
                <FormControl sx={{ mx: 1, width: '90%' }}>
                  <InputLabel>Select Firm</InputLabel>
                  <Select
                    label='Select Firm'
                    value={selectedCompany}
                    onChange={handleCompanyChange}
                    size='small'
                  >
                    {decodedData?.all_companies_info.map((company) => (
                      <MenuItem
                        key={company.company_id}
                        value={company.company_id}
                      >
                        {company.company_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )
            )}

            <List>
              {pages.map((page) => (
                <ListItem
                  button
                  key={page.name}
                  onClick={() => {
                    checkOnboarding(page.path);
                    setOpenDrawer(false);
                  }}
                >
                  <ListItemText primary={page.name} />
                </ListItem>
              ))}
            </List>
            <Button
              variant='contained'
              sx={{ ml: 2, width: '88%' }}
              onClick={() =>
                loginUser ? setOpenLogoutDialog(true) : navigate('/login')
              }
            >
              {loginUser ? 'Logout' : 'Login'}
            </Button>
          </Box>
        </Drawer>
        <SimpleDialog
          open={openLogoutDialog}
          onClose={handleClose}
        />
      </Container>
    </AppBar>
  );
}

export default TopNav;
